'use client';

import useUser from '@shared/utils/hooks/useUser';
import { type ReactNode, useRef, useState } from 'react';

import Confirmation from '../components/common/Confirmation';

type PublicAction = 'post' | 'upvote' | 'reply' | 'follow' | 'tag' | 'message';

type ConfirmContext = {
  isSpaceOrFactiii?: boolean;
};

type PendingState = {
  action: PublicAction;
  isSpaceOrFactiii: boolean;
};

const ACTION_LABEL: Record<PublicAction, string> = {
  post: 'posting',
  upvote: 'upvoting',
  reply: 'replying',
  follow: 'following',
  tag: 'tagging someone',
  message: 'sending this message'
};

export const useConfirmIfPrivate = () => {
  const { currentUser } = useUser();
  const [pending, setPending] = useState<PendingState | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const ensureConfirmed = (action: PublicAction, ctx?: ConfirmContext) =>
    new Promise<boolean>((resolve) => {
      if (!currentUser?.isPrivate) {
        resolve(true);
        return;
      }
      resolverRef.current = resolve;
      setPending({ action, isSpaceOrFactiii: ctx?.isSpaceOrFactiii ?? false });
    });

  const settle = (ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setPending(null);
  };

  const label = pending ? ACTION_LABEL[pending.action] : '';
  const dialog: ReactNode = pending ? (
    <Confirmation
      title={
        pending.isSpaceOrFactiii
          ? "Post Won't Be Visible to Others"
          : "You're a private account"
      }
      message={
        pending.isSpaceOrFactiii
          ? "Your account is private. Posts you make to spaces or factiiis will only be visible to you — other members won't be able to see them. Continue?"
          : `Your account is marked private, which hides you from public search, the map, and community discovery. ${label.charAt(0).toUpperCase() + label.slice(1)} may still be visible to others through already-delivered push notifications, emails, or screenshots we can't retract. Continue?`
      }
      actionButtonText="Continue"
      cancelHandler={() => settle(false)}
      confirmHandler={() => settle(true)}
    />
  ) : null;

  return { ensureConfirmed, dialog };
};
